























































































































































import { Component, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { MessageModule } from '@/store/modules/message';
import { AgentTypeEnum, IDateRange, CountryEnum } from '@/api/types';
import { search_messages } from '@/api/message';
import { list_agent_summaries, listAgentSummaries } from '@/api/agent';
import {
  formatDate,
  stringFromIDate,
  stringToIDate,
  formatApiError,
  translateComponentResource,
  compareString,
} from '@/utils/index';
import { AppRoleType } from '@/utils/types';

type ItemType = {
  text: string;
  value: string;
};

@Component({
  name: 'MessageListSearch',
})
export default class MessageListSearch extends Vue {
  public static readonly messageSearchEvent = 'message-search';

  private allAgentCountries: {
    [country: string]: string[];
  } = {};
  private valid = false;
  private openPanels = [0];
  private countries: CountryEnum[] = [];
  private countryItems: ItemType[] = [];
  private agentIds: string[] = [];
  private allAgentItems: { [agentId: string]: ItemType } = {};
  private agentItems: ItemType[] = [];
  private types: search_messages.Type[] = [];
  private typeItems: ItemType[] = [];
  private fromMenu = false;
  private fromDate: string | null = null;
  private fromDateRules: ((v: string[]) => boolean | string)[] = [];
  private toMenu = false;
  private toDate: string | null = null;
  private unreadOnly = false;

  get canShowAgent() {
    return UserModule.roles.some(r => (r as AppRoleType) !== AppRoleType.AGENT);
  }

  get canSearch() {
    return this.valid;
  }

  created() {
    this.loadMessageListSearchForm();
  }

  mounted() {
    this.fetchData();
    this.$nextTick(() => {
      this.loadRules();
      this.$nextTick(() => {
        if (this.canSearch) {
          this.search();
        }
      });
    });
  }

  private loadRules() {
    this.fromDateRules = [
      (v: string[]) =>
        (!!v && v.length > 0) ||
        (this.$t('{msg} is required', {
          msg: this.$t('From date'),
        }) as string),
    ];
  }

  search() {
    const req: search_messages.Request = {};
    if (this.canShowAgent) {
      req.agentIds = this.agentIds;
    }
    req.types = this.types;
    if (this.fromDate || this.toDate) {
      req.dateRange = {} as IDateRange;
      this.fromDate && (req.dateRange!.start = stringToIDate(this.fromDate)!);
      this.toDate && (req.dateRange!.end = stringToIDate(this.toDate)!);
    }
    req.unreadOnly = this.unreadOnly;

    this.$emit(MessageListSearch.messageSearchEvent, req);

    MessageModule.SaveMessageListSearchForm({
      countries: this.countries,
      agentIds: req.agentIds && req.agentIds.length > 0 ? req.agentIds : null,
      types: req.types,
      dateRange: req.dateRange ? req.dateRange : null,
      unreadOnly: req.unreadOnly,
    });
  }

  reset() {
    MessageModule.ClearMessageListSearchForm();
    (this.$refs.form as any).reset();
  }

  private loadAgentItemsByCountries(countries: CountryEnum[]) {
    const agentItems: ItemType[] = [];
    if (countries && countries.length > 0) {
      countries.forEach(c => {
        (this.allAgentCountries[c] || []).forEach(id => {
          agentItems.push(this.allAgentItems[id]);
        });
      });
    } else {
      (Object.values(this.allAgentCountries) || []).forEach(ids =>
        ids.forEach(id => agentItems.push(this.allAgentItems[id]))
      );
    }
    this.agentItems = agentItems;
  }

  private onCountriesChanged(countries: CountryEnum[]) {
    this.loadAgentItemsByCountries(countries);

    if (countries.length === 0) {
      this.agentIds = [];
    } else {
      let agentIds: string[] = [];
      countries.forEach(c => {
        if (this.allAgentCountries[c]) {
          agentIds = agentIds.concat(this.allAgentCountries[c]);
        }
      });
      this.agentIds = agentIds;
    }
  }

  private loadMessageListSearchForm() {
    const countries = MessageModule.messageListSearchForm.countries;
    this.countries = countries && countries.length > 0 ? countries : [];

    const agentIds = MessageModule.messageListSearchForm.agentIds;
    this.agentIds = agentIds && agentIds.length > 0 ? agentIds : [];

    const types = MessageModule.messageListSearchForm.types;
    this.types = types;

    const dateRange = MessageModule.messageListSearchForm.dateRange;
    this.fromDate =
      dateRange && dateRange.start ? stringFromIDate(dateRange.start) : null;
    this.toDate =
      dateRange && dateRange.end ? stringFromIDate(dateRange.end) : null;
    if (!this.fromDate && !this.toDate) {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      this.fromDate = formatDate(d);
    }

    this.unreadOnly = MessageModule.messageListSearchForm.unreadOnly;
  }

  private async fetchData() {
    AppModule.StartProgress();
    this.loadTypeItems();
    await this.loadAllAgentItems();
    this.loadCountryItems();
    this.loadAgentItemsByCountries(this.countries);
    AppModule.StopProgress();
  }

  private async loadAllAgentItems() {
    if (this.canShowAgent) {
      try {
        const listAgentsRes = await listAgentSummaries({
          agentTypes: Object.values(AgentTypeEnum).filter(
            t => (t as AgentTypeEnum) !== AgentTypeEnum.UNKNOWN_AGENT_TYPE
          ),
        } as list_agent_summaries.Request);
        const allItems: { [agentId: string]: ItemType } = {};
        listAgentsRes.agentSummaries.forEach(a => {
          allItems[a.id] = {
            text: a.name,
            value: a.id,
          };
          if (!this.allAgentCountries[a.country]) {
            this.allAgentCountries[a.country] = [];
          }
          this.allAgentCountries[a.country].push(a.id);
        });
        this.allAgentItems = allItems;
      } catch (err) {
        this.$toast.error(
          this.$t('Failed to {do} due to {err}', {
            do: (this.$t('Fetch {msg}', {
              msg: this.$t('Agent'),
            }) as string).toLowerCase(),
            err: formatApiError(this.$i18n, err),
          }) as string,
          {
            icon: 'mdi-alert',
          }
        );
      }
    }
  }

  private loadCountryItems() {
    if (this.canShowAgent) {
      this.countryItems = Object.values(CountryEnum)
        .map(c => ({
          text: `${this.$t('CountryEnum.' + c)}(${c})`,
          value: c,
        }))
        .sort((c1, c2) =>
          this.allAgentCountries[c1.value]
            ? this.allAgentCountries[c2.value]
              ? compareString(c1.value, c2.value)
              : -1
            : this.allAgentCountries[c2.value]
            ? 1
            : compareString(c1.value, c2.value)
        ) as ItemType[];
    }
  }

  private loadTypeItems() {
    this.typeItems = [
      {
        text: this.$t(`MessageTypeEnum.${search_messages.Type.SENT}`) as string,
        value: search_messages.Type.SENT,
      },
      {
        text: this.$t(
          `MessageTypeEnum.${search_messages.Type.RECEIVED}`
        ) as string,
        value: search_messages.Type.RECEIVED,
      },
    ];
  }

  private fromAllowedDates(val: string | null) {
    return !val || !this.toDate || val <= this.toDate;
  }

  private toAllowedDates(val: string | null) {
    return !val || !this.fromDate || val >= this.fromDate;
  }

  private translateComponentResource(resource: string) {
    return translateComponentResource(
      this.$i18n,
      this.$options.name as string,
      resource
    );
  }
}
